import { faQuestionCircle } from "@fortawesome/pro-light-svg-icons";
import React, { useEffect, useMemo } from "react";
import { DropdownItem } from "src/components/Dropdown/types";
import SelectSearch, { OptionItem } from "src/components/SelectSearch";
import { InputRow, HintIcon } from "../styles";
import { useIsMobile } from "src/hooks/useIsMobile";
import { ServiceTypeItem } from "src/types";
import Input from "src/components/Input";
import { useLocation } from "react-router-dom";
import { WorkRequestServiceTypeSelectSearchProps } from "../types";
import { objToArray } from "src/utils/objToArray";
import { getServiceTypesForClient } from "src/utils/serviceTypes";

const WorkRequestServiceTypeSelectSearch = ({
  serviceType,
  serviceTypeId,
  isVerifyMode,
  clientId,
  isManager,
  workTypes: _workTypes,
  serviceTypes,
  setFormData,
  disabled,
  noAuthServiceList,
  setServiceTypeId,
}: WorkRequestServiceTypeSelectSearchProps) => {
  const { isMobile } = useIsMobile();
  const location = useLocation();

  const defaultServiceType = location?.state?.serviceType;

  const serviceTypeSource = useMemo(() => {
    const managerSource = isManager && clientId ? getServiceTypesForClient(serviceTypes, clientId) : [];
    return (isManager ? managerSource : noAuthServiceList) || [];
  }, [clientId, isManager, noAuthServiceList, serviceTypes]);

  const getPrimaryClassification = (selectedServiceTypeId: string) => {
    const selectedServiceType = (serviceTypeSource as ServiceTypeItem[]).find(
      (item) => item.id === selectedServiceTypeId,
    );
    const classifications = objToArray(selectedServiceType?.classifications, (item, id) => ({
      ...item,
      id,
    })).filter((item) => item.showOnPublic !== false);

    const primaryClassification = classifications[0];
    if (!primaryClassification) return null;

    return {
      id: String(primaryClassification.name || primaryClassification.id || ""),
      label: primaryClassification.description || primaryClassification.name || "",
    };
  };

  const dropdownServiceTypes = useMemo(() => {
    return (serviceTypeSource as ServiceTypeItem[])
      ?.filter(
        (item: ServiceTypeItem) =>
          item.showOnPublic !== false &&
          Object.values(item?.classifications || {}).some((classification) => classification?.showOnPublic !== false),
      )
      .map((item: ServiceTypeItem) => {
        return {
          id: item.id,
          value: item.description,
          longDescription: item.longDescription,
        };
      });
  }, [serviceTypeSource]);

  useEffect(() => {
    if (!serviceTypeId) return;

    const primaryClassification = getPrimaryClassification(serviceTypeId);
    if (!primaryClassification) return;

    setFormData((data) => {
      if (data.serviceType === serviceTypeId && data.classification === primaryClassification.label) {
        return data;
      }

      return {
        ...data,
        serviceType: serviceTypeId,
        classification: primaryClassification.label,
      };
    });
    // setClassificationId(primaryClassification.id);
  }, [serviceTypeId, serviceTypeSource]);

  if (disabled) {
    return (
      <InputRow $isMobile={isMobile}>
        <label>Service Type:</label>
        <div>
          <Input placeholder={"Select Service Type"} disabled value={serviceType}></Input>
        </div>

        <HintIcon
          style={{
            visibility: defaultServiceType?.longDescription ? "visible" : "hidden",
          }}
          title="Edit Mode"
          data-tip={
            defaultServiceType?.longDescription ? (defaultServiceType?.longDescription).replace(/<br ?\/?>/g, "\n") : ""
          }
          icon={faQuestionCircle}
        />
      </InputRow>
    );
  }

  return (
    <InputRow $isMobile={isMobile} $isSelectSearch>
      <label>Service Type:</label>
      <SelectSearch
        defaultValue={serviceType}
        placeholder={"Select Service Type"}
        disabled={isVerifyMode || !clientId}
        $isMobile={isMobile}
        options={dropdownServiceTypes as OptionItem[]}
        onChange={(item: DropdownItem) => {
          const selectedServiceTypeId = String(item.id);
          const primaryClassification = getPrimaryClassification(selectedServiceTypeId);

          setFormData((data) => ({
            ...data,
            serviceType: selectedServiceTypeId,
            classification: primaryClassification?.label || "",
          }));
          setServiceTypeId(selectedServiceTypeId);
          // setClassificationId(primaryClassification?.id || "");
        }}
        bottomResultsGap={43}
      />

      <HintIcon
        style={{
          visibility: !!dropdownServiceTypes?.find((el) => el.id === serviceType)?.longDescription
            ? "visible"
            : "hidden",
        }}
        data-tip={`<div style="overflow:hidden">${(
          dropdownServiceTypes?.find((el) => el.id === serviceType)?.longDescription || ""
        ).replace(/<br ?\/?>/g, "\n")}</div>`}
        icon={faQuestionCircle}
      />
    </InputRow>
  );
};

export default WorkRequestServiceTypeSelectSearch;
